import React from "react";
import { Stack, Text } from "@mantine/core";
import { AmountType } from "../../types/commission";
import { formatCommissionAmount } from "../../utils/commissionUtils";

/** Static pieces of NewCommissionForm (method options + help, date and
 * amount display helpers), kept apart so the form stays readable. */

export const CALC_METHOD_OPTIONS = [
  { value: "STANDARD", label: "Standard" },
  { value: "COMMISSION_ONLY", label: "Commission Only" },
  { value: "GREATER_THAN", label: "Greater Than" },
  { value: "NUM_SERVICES", label: "Threshold" },
  { value: "TIERED", label: "Tiered / Step" },
];

export const CalcMethodTooltip = () => (
  <Stack gap={8}>
    <Text size="sm" c="white">
      <Text size="sm" span fw={600} c="white">
        Standard:
      </Text>{" "}
      Applies commissions on all service and product transactions.
    </Text>
    <Text size="sm" c="white">
      <Text size="sm" span fw={600} c="white">
        Commission Only:
      </Text>{" "}
      Staff earn only commission on services and products, with no base hourly
      pay.
    </Text>
    <Text size="sm" c="white">
      <Text size="sm" span fw={600} c="white">
        Greater Than:
      </Text>{" "}
      Commissions apply only when they exceed the staff member's hourly rate.
    </Text>
    <Text size="sm" c="white">
      <Text size="sm" span fw={600} c="white">
        Threshold:
      </Text>{" "}
      Calculated on a daily basis. Staff are paid hourly if they service under{" "}
      <i>X</i> number of services that day. Otherwise, they are paid on
      commission.
    </Text>
    <Text size="sm" c="white">
      <Text size="sm" span fw={600} c="white">
        Tiered / Step:
      </Text>{" "}
      Staff earn escalating rates as their period earnings grow. Once a category
      total reaches a tier, that tier's rate applies to the entire category
      total.
    </Text>
  </Stack>
);

export const formatDateToString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const parseDateString = (dateStr: string): Date => {
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Convert a CommissionRecord's amount fields to a display string for CommissionInput.
 * e.g. { amount: 15, type: "Percentage" } → "15%", { amount: 25, type: "Fixed Amount" } → "$25"
 */
export const toDisplayValue = (
  amount: number | null,
  amountType: string | null,
): string => {
  if (amount === null || amount === undefined || !amountType) return "";
  return formatCommissionAmount(amount, amountType as AmountType | null) || "";
};
